import React from "react";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { ChartsLegend } from "./ChartsLegend";
import { StandortAuslastungItem } from "../../models/admin-dashboard-entities";
import { Chart as ChartJS } from "chart.js";

type Props = {
  labelMaxLength: number;
  inputData: Record<string, StandortAuslastungItem>;
};

// Static configuration
const COLORED_VALUE_TITLE = "Abrechenbare Mitglieder";
const GRAY_VALUE_TITLE = "Kapazität";
const ALL_MITGLIEDER_TITLE = "Alle Mitglieder";

const MultiColorLabels = {
  id: "multiColorLabels",
  afterDatasetsDraw(chart: ChartJS) {
    const { ctx } = chart;

    // find the gray dataset (last one = Kapazität)
    const datasetIndex = chart.data.datasets.length - 1;
    const meta = chart.getDatasetMeta(datasetIndex);
    if (!meta || !meta.data) return;

    meta.data.forEach((bar: any, index: number) => {
      const usedPct = chart.data.datasets[0].data[index] as number; // Abrechenbare
      const allExtra = chart.data.datasets[1].data[index] as number; // Alle (extra part)

      const totalPct = usedPct + allExtra; // sum of teal + orange

      const text1 = `${Math.round(usedPct)}%`;
      const text2 = ` / ${Math.round(totalPct)}%`;

      const { x, y } = bar.getProps(["x", "y"], true);

      ctx.save();
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.font = "12px sans-serif";

      ctx.fillStyle = "#44AEB0";
      ctx.fillText(text1, x + 4, y);

      const offset = ctx.measureText(text1).width;

      // orange for all
      ctx.fillStyle = "#FF8C00";
      ctx.fillText(text2, x + 4 + offset, y);

      ctx.restore();
    });
  },
};

const LocationDashboardChart: React.FC<Props> = ({
  labelMaxLength,
  inputData,
}) => {
  const entries = Object.entries(inputData).sort(
    ([_, dataA], [__, dataB]) => dataB.kapa - dataA.kapa // sort by capacity desc
  );
  const labels = entries.map(([label]) =>
    label.length > labelMaxLength
      ? `${label.substring(0, labelMaxLength)}…`
      : label
  );

  // build base arrays
  const used = entries.map(([, d]) => Number(d.abrechenbareMitglieder) || 0);
  const all = entries.map(([, d]) => Number(d.alleMitglieder) || 0);
  const cap = entries.map(([, d]) => Number(d.kapa) || 0);

  // overflow flag per row
  const isOverflow = used.map((u, i) => cap[i] > 0 && u > cap[i]);

  // Percent-based stacking
  const pctUsedRaw = used.map((u, i) => (cap[i] > 0 ? (u / cap[i]) * 100 : 0));
  const pctAllRaw = all.map((a, i) => (cap[i] > 0 ? (a / cap[i]) * 100 : 0));

  const pctUsed = pctUsedRaw.map((p) => Math.max(0, Math.min(100, p)));
  const pctAll = pctAllRaw.map((p) => Math.max(0, Math.min(100, p)));
  const pctRem = pctAll.map((p) => (p < 100 ? 100 - p : 0));

  const data: any = {
    labels,
    datasets: [
      {
        label: COLORED_VALUE_TITLE,
        data: pctUsed,
        backgroundColor: (_ctx: any) => {
          const i = _ctx.dataIndex;
          return isOverflow[i] ? "#D9534F" : "#44AEB0";
        },
        borderColor: (_ctx: any) => {
          const i = _ctx.dataIndex;
          return isOverflow[i] ? "#D9534F" : "#44AEB0";
        },
        barThickness: 12,
        stack: "total",
      },
      {
        label: ALL_MITGLIEDER_TITLE,
        data: pctAll.map((p, i) => Math.max(0, p - pctUsed[i])), // stacked on top
        backgroundColor: "#FF8C00", // orange
        borderColor: "#FF8C00",
        barThickness: 12,
        stack: "total",
      },
      {
        label: GRAY_VALUE_TITLE,
        data: pctRem,
        backgroundColor: "#C1C9CD",
        borderColor: "#C1C9CD",
        barThickness: 12,
        stack: "total",
      },
    ],
    displayedLongLabels: entries.map(([full]) => full),
    originalUsed: used,
    originalCapacity: cap,
    originalAll: all,
    pctUsedRaw,
    isOverflow,
  };
  // Canvas height based on rows (keeps bars readable)
  const barsAreaHeight = 20 * entries.length + 24; // + small padding

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Scroll only the bars area */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <div style={{ height: barsAreaHeight, width: "100%" }}>
          <Bar
            data={data}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              indexAxis: "y",
              interaction: { mode: "index", axis: "y", intersect: false },
              elements: { bar: { borderWidth: 2 } },
              layout: { padding: { right: 40, top: 0, bottom: 0, left: 0 } },
              scales: {
                x: {
                  min: 0,
                  max: 125,
                  display: false,
                  stacked: true,
                  grid: { display: false },
                },
                y: {
                  stacked: true,
                  grid: { display: false },
                },
              },
              plugins: {
                legend: { display: false },
                tooltip: {
                  position: "nearest",
                  callbacks: {
                    title: (ctx: any) =>
                      (data as any).displayedLongLabels[ctx[0].dataIndex],
                    label: (ctx: any) => {
                      const i = ctx.dataIndex;
                      const datasetLabel = ctx.dataset.label;

                      if (datasetLabel === GRAY_VALUE_TITLE) {
                        return `Kapazität: ${cap[i]}`;
                      }
                      if (datasetLabel === ALL_MITGLIEDER_TITLE) {
                        return `${ALL_MITGLIEDER_TITLE}: ${all[i]} / ${cap[i]}`;
                      }
                      if (datasetLabel === COLORED_VALUE_TITLE) {
                        return `${COLORED_VALUE_TITLE}: ${used[i]} / ${cap[i]}`;
                      }
                      return "";
                    },
                  },
                },
                datalabels: {
                  color: "#444",
                  font: { size: 12 },
                  padding: 4,
                  anchor: "end",
                  align: "end", // at the end of the gray segment (value 100)
                  offset: 0,
                  clamp: true,
                  clip: true, // keep inside canvas now that we have right gap
                  formatter: () => null,
                },
              },
            }}
            plugins={[ChartDataLabels, MultiColorLabels]}
          />
        </div>
      </div>

      {/* Legend is outside the scroll area -> never cut off */}
      <div style={{ paddingTop: 8 }}>
        <ChartsLegend
          usedValueTitle={COLORED_VALUE_TITLE}
          capValueTitle={GRAY_VALUE_TITLE}
          additionalValueTitle={ALL_MITGLIEDER_TITLE}
          additionalValueBgColor="#FF8C00"
        />
      </div>
    </div>
  );
};

export default LocationDashboardChart;
